import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function UserCreate({ userInfo }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    employeeId: '',
    name: '',
    password: '',
    confirmPassword: '',
    role: 'user'
  });
  const [groupAttributes, setGroupAttributes] = useState({
    client_company_id: '',
    job_assignment_id: '',
    employment_type_id: ''
  });
  const [clientCompanies, setClientCompanies] = useState([]);
  const [jobAssignments, setJobAssignments] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // スーパー管理者権限チェック
    if (!userInfo || userInfo.role !== 'super_admin') {
      navigate('/admin');
      return;
    }
    
    // グループ属性関連のデータを取得
    fetchClientCompanies();
    fetchEmploymentTypes();
  }, [userInfo, navigate]);
  
  // クライアント企業が変更されたときに担当業務を取得
  useEffect(() => {
    if (groupAttributes.client_company_id) {
      fetchJobAssignments(groupAttributes.client_company_id);
    }
  }, [groupAttributes.client_company_id]);
  
  // クライアント企業の取得
  const fetchClientCompanies = async () => {
    try {
      const response = await fetch('/api/admin/client-companies', {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('クライアント企業の取得に失敗しました');
      }

      const data = await response.json();
      setClientCompanies(data);
    } catch (error) {
      console.error('Error fetching client companies:', error);
      setError('クライアント企業の取得に失敗しました');
    }
  };
  
  // 担当業務の取得
  const fetchJobAssignments = async (clientCompanyId) => {
    try {
      const response = await fetch(`/api/admin/client-companies/${clientCompanyId}/job-assignments`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('担当業務の取得に失敗しました');
      }

      const data = await response.json();
      setJobAssignments(data);
    } catch (error) {
      console.error('Error fetching job assignments:', error);
      setError('担当業務の取得に失敗しました');
    }
  };
  
  // 雇用区分の取得
  const fetchEmploymentTypes = async () => {
    try {
      const response = await fetch('/api/admin/employment-types', {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('雇用区分の取得に失敗しました');
      }

      const data = await response.json();
      setEmploymentTypes(data);
    } catch (error) {
      console.error('Error fetching employment types:', error);
      setError('雇用区分の取得に失敗しました');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleGroupAttributeChange = (e) => {
    const { name, value } = e.target;
    setGroupAttributes(prev => ({
      ...prev,
      [name]: value
    }));
    
    // クライアント企業が変更された場合、担当業務をリセット
    if (name === 'client_company_id') {
      setGroupAttributes(prev => ({
        ...prev,
        job_assignment_id: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // バリデーション
    if (formData.password !== formData.confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    if (formData.password.length < 6) {
      setError('パスワードは6文字以上で入力してください');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // ユーザー登録
      const response = await fetch('/api/admin/users', {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          employeeId: formData.employeeId,
          name: formData.name,
          password: formData.password,
          role: formData.role
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'ユーザー登録に失敗しました');
      }
      
      // 登録されたユーザーの情報を取得
      const userData = await response.json();
      
      // グループ属性が設定されている場合は更新
      if (groupAttributes.client_company_id || groupAttributes.job_assignment_id || groupAttributes.employment_type_id) {
        const attributesResponse = await fetch(`/api/admin/users/${userData.id}/group-attributes`, {
          method: 'PUT',
          headers: {
            'Authorization': localStorage.getItem('token'),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(groupAttributes)
        });
        
        if (!attributesResponse.ok) {
          console.error('グループ属性の設定に失敗しました');
          // ユーザー登録自体は成功しているので、エラーをスローせずに警告だけ表示
          alert('ユーザーは登録されましたが、グループ属性の設定に失敗しました');
        }
      }

      // 登録成功
      alert('ユーザーを登録しました');
      navigate('/admin');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4">
        <button 
          onClick={() => navigate('/admin')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
        >
          ← 戻る
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6 max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">新規ユーザー登録</h1>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="employeeId">
              社員ID
            </label>
            <input
              id="employeeId"
              name="employeeId"
              type="text"
              value={formData.employeeId}
              onChange={handleChange}
              required
              className="w-full p-2 border rounded"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="name">
              名前
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full p-2 border rounded"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="password">
              パスワード
            </label>
            <input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full p-2 border rounded"
            />
            <p className="text-sm text-gray-500 mt-1">6文字以上で入力してください</p>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="confirmPassword">
              パスワード（確認）
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="w-full p-2 border rounded"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="role">
              権限
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full p-2 border rounded"
            >
              <option value="user">ユーザー</option>
              <option value="admin">管理者</option>
              <option value="super_admin">スーパー管理者</option>
            </select>
          </div>
          
          <h2 className="text-lg font-semibold mb-4 mt-6 border-t pt-4">グループ属性設定</h2>
          
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="client_company_id">
              クライアント企業
            </label>
            <select
              id="client_company_id"
              name="client_company_id"
              value={groupAttributes.client_company_id || ''}
              onChange={handleGroupAttributeChange}
              className="w-full p-2 border rounded"
            >
              <option value="">選択してください</option>
              {clientCompanies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="job_assignment_id">
              担当業務
            </label>
            <select
              id="job_assignment_id"
              name="job_assignment_id"
              value={groupAttributes.job_assignment_id || ''}
              onChange={handleGroupAttributeChange}
              className="w-full p-2 border rounded"
              disabled={!groupAttributes.client_company_id}
            >
              <option value="">選択してください</option>
              {jobAssignments.map(job => (
                <option key={job.id} value={job.id}>
                  {job.name}
                </option>
              ))}
            </select>
            {!groupAttributes.client_company_id && (
              <p className="text-sm text-gray-500 mt-1">
                先にクライアント企業を選択してください
              </p>
            )}
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 mb-2" htmlFor="employment_type_id">
              雇用区分
            </label>
            <select
              id="employment_type_id"
              name="employment_type_id"
              value={groupAttributes.employment_type_id || ''}
              onChange={handleGroupAttributeChange}
              className="w-full p-2 border rounded"
            >
              <option value="">選択してください</option>
              {employmentTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
          >
            {loading ? '処理中...' : '登録する'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default UserCreate; 